import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Save, Wand2, AlertTriangle, CheckCircle2 } from "lucide-react";
import { useCompany } from "../../context/CompanyContext";
import {
  ensurePayrollBootstrap,
  ensurePayrollJournalBootstrap,
  getPayrollSettings,
  updatePayrollSettings,
  getPayrollJournalMappings,
  upsertPayrollJournalMapping,
  getCompanyCoaAccounts,
} from "../payrollService";

const initialSettings = {
  payslip_prefix: "KPY",
  payroll_frequency: "monthly",
  currency_code: "NGN",
  working_days_per_month: 22,
  working_hours_per_day: 8,
};

const MAPPING_RULES = {
  GROSS_PAY_EXPENSE: {
    expected: "expense",
    side: "Debit",
    accountingTreatment:
      "Debit payroll salaries and wages expense for the gross pay earned by employees.",
    keywords: [
      "salary",
      "salaries",
      "wages",
      "payroll expense",
      "staff cost",
      "staff salary",
      "salary expense",
      "wages expense",
      "employee compensation",
    ],
  },

  EMPLOYER_CONTRIBUTION_EXPENSE: {
    expected: "expense",
    side: "Debit",
    accountingTreatment:
      "Debit employer payroll contribution expense for employer pension, HMO/NHIS or other employer obligations.",
    keywords: [
      "pension expense",
      "staff benefit expense",
      "benefit expense",
      "employer contribution",
      "payroll contribution",
      "staff welfare",
    ],
  },

  NET_SALARY_PAYABLE: {
    expected: "liability",
    side: "Credit",
    accountingTreatment:
      "Credit salary/payroll payable for the net amount owed to employees.",
    keywords: [
      "salary payable",
      "salaries payable",
      "payroll payable",
      "wages payable",
      "staff payable",
    ],
  },

  PAYE_PAYABLE: {
    expected: "liability",
    side: "Credit",
    accountingTreatment:
      "Credit PAYE/tax payable for employee tax withheld and due to tax authority.",
    keywords: [
      "paye payable",
      "income tax payable",
      "tax payable",
      "current tax payable",
      "paye",
    ],
  },

  PENSION_EMPLOYEE_PAYABLE: {
    expected: "liability",
    side: "Credit",
    accountingTreatment:
      "Credit pension payable for employee pension deductions withheld.",
    keywords: [
      "pension payable",
      "employee pension payable",
      "rsa payable",
      "retirement payable",
      "pension employee",
    ],
  },

  PENSION_EMPLOYER_PAYABLE: {
    expected: "liability",
    side: "Credit",
    accountingTreatment:
      "Credit pension payable for employer pension contribution obligation.",
    keywords: [
      "pension payable",
      "employer pension payable",
      "rsa payable",
      "retirement payable",
      "pension employer",
    ],
  },

  NHF_PAYABLE: {
    expected: "liability",
    side: "Credit",
    accountingTreatment:
      "Credit NHF payable for housing fund deductions withheld.",
    keywords: [
      "nhf payable",
      "housing fund payable",
      "national housing fund",
      "nhf",
    ],
  },

  NHIS_PAYABLE: {
    expected: "liability",
    side: "Credit",
    accountingTreatment:
      "Credit NHIS/HMO payable for health insurance obligations.",
    keywords: [
      "nhis payable",
      "health insurance payable",
      "health scheme payable",
      "hmo payable",
      "nhis",
    ],
  },

  EMPLOYEE_LOAN_RECEIVABLE: {
    expected: "asset",
    side: "Credit",
    accountingTreatment:
      "Credit staff loan/employee advance receivable because loan deduction reduces the asset owed by employees.",
    keywords: [
      "staff loan",
      "employee loan",
      "loan receivable",
      "staff advance",
      "employee advance",
      "salary advance",
    ],
  },

  LOAN_DEDUCTION_PAYABLE: {
    expected: "asset",
    side: "Credit",
    accountingTreatment:
      "For payroll loan recovery, use staff loan/employee advance receivable, not a payable account.",
    keywords: [
      "staff loan",
      "employee loan",
      "loan receivable",
      "staff advance",
      "employee advance",
      "salary advance",
    ],
  },

  OTHER_DEDUCTION_PAYABLE: {
    expected: "liability",
    side: "Credit",
    accountingTreatment:
      "Credit other payroll deductions payable for amounts withheld from employees but payable to a third party.",
    keywords: [
      "other deductions payable",
      "payroll deductions payable",
      "staff deductions payable",
      "other payables",
    ],
  },

  OTHER_STATUTORY_PAYABLE: {
    expected: "liability",
    side: "Credit",
    accountingTreatment:
      "Credit other statutory payable for statutory remittances not separately mapped.",
    keywords: [
      "statutory payable",
      "other statutory payable",
      "statutory deductions payable",
      "government remittances",
    ],
  },

  OTHER_EMPLOYER_CONTRIBUTION_PAYABLE: {
    expected: "liability",
    side: "Credit",
    accountingTreatment:
      "Credit other employer contribution payable for employer obligations not separately mapped.",
    keywords: [
      "employer contribution payable",
      "staff benefit payable",
      "benefit payable",
      "payroll contribution payable",
      "other payables",
    ],
  },
};

function normalizeText(value) {
  return String(value || "")
    .trim()
    .toLowerCase();
}

function accountLabel(account) {
  const code = account.account_number ? `${account.account_number} - ` : "";
  const type = account.type ? ` (${account.type})` : "";
  const category = account.category ? ` / ${account.category}` : "";
  return `${code}${account.name || "Unnamed account"}${type}${category}`;
}

function classifyAccount(account) {
  const text = `${normalizeText(account?.type)} ${normalizeText(
    account?.category
  )} ${normalizeText(account?.name)} ${normalizeText(account?.account_number)}`;

  if (
    text.includes("asset") ||
    text.includes("receivable") ||
    text.includes("advance") ||
    text.includes("staff loan") ||
    text.includes("employee loan")
  ) {
    return "asset";
  }

  if (
    text.includes("expense") ||
    text.includes("cost") ||
    text.includes("overhead") ||
    text.includes("salaries") ||
    text.includes("salary expense") ||
    text.includes("wages expense") ||
    text.includes("staff cost")
  ) {
    return "expense";
  }

  if (
    text.includes("liability") ||
    text.includes("payable") ||
    text.includes("creditor") ||
    text.includes("current liabilities")
  ) {
    return "liability";
  }

  return "unknown";
}

function getSuggestionForMapping(mappingCode, accounts) {
  const rule = MAPPING_RULES[mappingCode];
  if (!rule) return null;

  const scored = accounts
    .map((account) => {
      const text = `${normalizeText(account.name)} ${normalizeText(
        account.type
      )} ${normalizeText(account.category)} ${normalizeText(
        account.account_number
      )}`;

      let score = 0;

      rule.keywords.forEach((keyword) => {
        if (text.includes(normalizeText(keyword))) score += 10;
      });

      const accountType = classifyAccount(account);

      if (accountType === rule.expected) score += 20;
      if (accountType !== rule.expected && accountType !== "unknown")
        score -= 100;

      if (text.includes("payroll")) score += 5;
      if (text.includes("salary")) score += 5;
      if (text.includes("wages")) score += 5;
      if (text.includes("pension")) score += 4;
      if (text.includes("tax")) score += 3;
      if (text.includes("paye")) score += 3;
      if (text.includes("statutory")) score += 3;

      return { account, score, accountType };
    })
    .filter((x) => x.score > 0 && x.accountType === rule.expected)
    .sort((a, b) => b.score - a.score);

  return scored[0]?.account || null;
}

function getMappingWarning(mappingCode, account) {
  if (!account) return null;

  const rule = MAPPING_RULES[mappingCode];
  if (!rule) return null;

  const actualType = classifyAccount(account);

  if (actualType === "unknown") {
    return `Account type could not be confidently classified. Expected ${rule.expected} account.`;
  }

  if (actualType !== rule.expected) {
    return `Accounting validation failed. This mapping expects a ${rule.expected} account, but the selected account looks like ${actualType}.`;
  }

  return null;
}

function getMappingStatus(row) {
  if (!row.account_id) return "not mapped";
  if (row.warning) return "needs review";
  return "configured";
}

export default function PayrollSettings() {
  const { companyId } = useCompany();

  const [busy, setBusy] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  const [settings, setSettings] = useState(initialSettings);
  const [mappings, setMappings] = useState([]);
  const [coaAccounts, setCoaAccounts] = useState([]);
  const [savingMapCode, setSavingMapCode] = useState("");
  const [autoMapping, setAutoMapping] = useState(false);

  const load = useCallback(async () => {
    if (!companyId) return;

    setBusy(true);
    setMsg("");

    try {
      await ensurePayrollBootstrap(companyId);
      await ensurePayrollJournalBootstrap(companyId);

      const [settingsData, mappingsData, coaData] = await Promise.all([
        getPayrollSettings(companyId),
        getPayrollJournalMappings(companyId),
        getCompanyCoaAccounts(companyId),
      ]);

      setSettings({
        payslip_prefix: settingsData?.payslip_prefix || "KPY",
        payroll_frequency: settingsData?.payroll_frequency || "monthly",
        currency_code: settingsData?.currency_code || "NGN",
        working_days_per_month: Number(
          settingsData?.working_days_per_month || 22
        ),
        working_hours_per_day: Number(settingsData?.working_hours_per_day || 8),
      });

      setMappings(mappingsData || []);
      setCoaAccounts(coaData || []);
    } catch (err) {
      setMsg(err.message || "Failed to load payroll settings");
    } finally {
      setBusy(false);
    }
  }, [companyId]);

  useEffect(() => {
    load();
  }, [load]);

  const mappingRows = useMemo(() => {
    return (mappings || []).map((row) => {
      const selectedAccount =
        coaAccounts.find((acc) => acc.id === row.account_id) || null;

      const suggestion = getSuggestionForMapping(row.mapping_code, coaAccounts);
      const warning = getMappingWarning(row.mapping_code, selectedAccount);
      const rule = MAPPING_RULES[row.mapping_code] || null;

      return {
        ...row,
        selectedAccount,
        suggestion,
        warning,
        expectedAccountType: rule?.expected || "unknown",
        normalSide: rule?.side || "-",
        accountingTreatment: rule?.accountingTreatment || "",
      };
    });
  }, [mappings, coaAccounts]);

  const checklist = useMemo(() => {
    const total = mappingRows.length;
    const configured = mappingRows.filter((m) => !!m.account_id && !m.warning);
    const missing = mappingRows.filter((m) => !m.account_id);
    const warnings = mappingRows.filter((m) => !!m.warning && !!m.account_id);

    return {
      total,
      configured: configured.length,
      missing,
      warnings,
      allReady: total > 0 && missing.length === 0 && warnings.length === 0,
    };
  }, [mappingRows]);

  const journalPreviewRows = useMemo(() => {
    return mappingRows.map((row) => ({
      mapping_code: row.mapping_code,
      mapping_name: row.mapping_name,
      side: row.normalSide,
      expected: row.expectedAccountType,
      selected: row.selectedAccount ? accountLabel(row.selectedAccount) : "-",
      status: getMappingStatus(row),
      treatment: row.accountingTreatment,
    }));
  }, [mappingRows]);

  async function handleSaveSettings(e) {
    e.preventDefault();
    if (!companyId) return;

    try {
      setSaving(true);
      setMsg("");

      await updatePayrollSettings(companyId, {
        payslip_prefix: settings.payslip_prefix || "KPY",
        payroll_frequency: settings.payroll_frequency || "monthly",
        currency_code: settings.currency_code || "NGN",
        working_days_per_month: Number(settings.working_days_per_month || 22),
        working_hours_per_day: Number(settings.working_hours_per_day || 8),
      });

      setMsg("Payroll settings saved successfully.");
    } catch (err) {
      setMsg(err.message || "Failed to save payroll settings");
    } finally {
      setSaving(false);
    }
  }

  async function handleMappingChange(mappingRow, accountId) {
    try {
      setSavingMapCode(mappingRow.mapping_code);
      setMsg("");

      const selectedAccount =
        coaAccounts.find((account) => account.id === accountId) || null;

      const warning = getMappingWarning(
        mappingRow.mapping_code,
        selectedAccount
      );

      if (accountId && warning) {
        setMsg(
          `${mappingRow.mapping_name} was not saved. ${warning} Please select a valid ${mappingRow.expectedAccountType} account.`
        );
        return;
      }

      await upsertPayrollJournalMapping({
        company_id: companyId,
        mapping_code: mappingRow.mapping_code,
        mapping_name: mappingRow.mapping_name,
        account_id: accountId || null,
        notes: mappingRow.notes || null,
      });

      setMappings((prev) =>
        prev.map((row) =>
          row.mapping_code === mappingRow.mapping_code
            ? { ...row, account_id: accountId || null }
            : row
        )
      );

      setMsg(`Mapping updated for ${mappingRow.mapping_name}.`);
    } catch (err) {
      setMsg(err.message || "Failed to save journal mapping");
    } finally {
      setSavingMapCode("");
    }
  }

  async function handleAutoMap() {
    try {
      setAutoMapping(true);
      setMsg("");

      const candidates = mappingRows.filter((row) => {
        if (row.account_id || !row.suggestion) return false;
        return !getMappingWarning(row.mapping_code, row.suggestion);
      });

      for (const row of candidates) {
        await upsertPayrollJournalMapping({
          company_id: companyId,
          mapping_code: row.mapping_code,
          mapping_name: row.mapping_name,
          account_id: row.suggestion.id,
          notes: row.notes || null,
        });
      }

      await load();

      setMsg(
        candidates.length
          ? `Auto-mapped ${candidates.length} accounting-safe payroll journal mapping(s).`
          : "No suitable accounting-safe auto-mapping suggestions were found."
      );
    } catch (err) {
      setMsg(err.message || "Failed to auto-map payroll journal accounts");
    } finally {
      setAutoMapping(false);
    }
  }

  return (
    <div className="payroll-page">
      {msg ? <div className="payroll-alert info">{msg}</div> : null}

      <div className="payroll-grid payroll-grid-4">
        <div className="payroll-card payroll-summary-card">
          <p className="payroll-summary-title">Mappings</p>
          <h3>{checklist.total}</h3>
          <span>Payroll journal mapping rows</span>
        </div>

        <div className="payroll-card payroll-summary-card">
          <p className="payroll-summary-title">Valid</p>
          <h3>{checklist.configured}</h3>
          <span>Mappings linked to correct COA type</span>
        </div>

        <div className="payroll-card payroll-summary-card">
          <p className="payroll-summary-title">Unconfigured</p>
          <h3>{checklist.missing.length}</h3>
          <span>Mappings still needing setup</span>
        </div>

        <div className="payroll-card payroll-summary-card">
          <p className="payroll-summary-title">Blocked</p>
          <h3>{checklist.warnings.length}</h3>
          <span>Mappings that fail accounting validation</span>
        </div>
      </div>

      <div className="payroll-card">
        <div className="payroll-card-header">
          <div>
            <h3>Payroll Settings</h3>
            <p>Maintain payroll defaults used across payroll processing.</p>
          </div>
        </div>

        <form className="payroll-form" onSubmit={handleSaveSettings}>
          <div className="payroll-grid payroll-grid-2">
            <label>
              Payslip Prefix
              <input
                value={settings.payslip_prefix}
                onChange={(e) =>
                  setSettings((p) => ({ ...p, payslip_prefix: e.target.value }))
                }
              />
            </label>

            <label>
              Payroll Frequency
              <select
                value={settings.payroll_frequency}
                onChange={(e) =>
                  setSettings((p) => ({
                    ...p,
                    payroll_frequency: e.target.value,
                  }))
                }
              >
                <option value="monthly">Monthly</option>
                <option value="weekly">Weekly</option>
                <option value="biweekly">Biweekly</option>
              </select>
            </label>

            <label>
              Currency Code
              <input
                value={settings.currency_code}
                onChange={(e) =>
                  setSettings((p) => ({ ...p, currency_code: e.target.value }))
                }
              />
            </label>

            <label>
              Working Days Per Month
              <input
                type="number"
                value={settings.working_days_per_month}
                onChange={(e) =>
                  setSettings((p) => ({
                    ...p,
                    working_days_per_month: e.target.value,
                  }))
                }
              />
            </label>

            <label>
              Working Hours Per Day
              <input
                type="number"
                step="0.01"
                value={settings.working_hours_per_day}
                onChange={(e) =>
                  setSettings((p) => ({
                    ...p,
                    working_hours_per_day: e.target.value,
                  }))
                }
              />
            </label>
          </div>

          <div className="payroll-form-actions">
            <button type="submit" className="primary-btn" disabled={saving}>
              <Save size={16} />
              {saving ? "Saving..." : "Save Settings"}
            </button>
          </div>
        </form>
      </div>

      <div className="payroll-card">
        <div className="payroll-card-header">
          <div>
            <h3>Required Mapping Checklist</h3>
            <p>
              Payroll can only post to journal when all required mappings are
              configured and mapped to the correct account type.
            </p>
          </div>

          <button
            type="button"
            className="ghost-btn"
            onClick={handleAutoMap}
            disabled={autoMapping}
          >
            <Wand2 size={16} />
            {autoMapping ? "Auto-Mapping..." : "Auto-Suggest & Map"}
          </button>
        </div>

        <div className="payroll-checklist">
          <div
            className={`payroll-checklist-banner ${
              checklist.allReady ? "ok" : "warn"
            }`}
          >
            {checklist.allReady ? (
              <>
                <CheckCircle2 size={18} />
                <span>
                  All required payroll journal mappings are configured and
                  accounting-safe. Posting is ready.
                </span>
              </>
            ) : (
              <>
                <AlertTriangle size={18} />
                <span>
                  {checklist.missing.length} missing mapping(s) and{" "}
                  {checklist.warnings.length} invalid mapping(s) must be fixed
                  before posting.
                </span>
              </>
            )}
          </div>

          {!checklist.allReady ? (
            <div className="payroll-checklist-list">
              {checklist.missing.map((row) => (
                <div key={row.mapping_code} className="payroll-checklist-item">
                  <span>{row.mapping_name}</span>
                  <span className="pill draft">required</span>
                </div>
              ))}

              {checklist.warnings.map((row) => (
                <div key={row.mapping_code} className="payroll-checklist-item">
                  <span>
                    {row.mapping_name}: {row.warning}
                  </span>
                  <span className="pill warning">invalid</span>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      </div>

      <div className="payroll-card">
        <div className="payroll-card-header">
          <div>
            <h3>Accounting Treatment Preview</h3>
            <p>
              This shows the normal debit or credit leg each payroll mapping
              will create when payroll is posted.
            </p>
          </div>
        </div>

        <div className="payroll-table-wrap">
          <table className="payroll-table">
            <thead>
              <tr>
                <th>Posting Side</th>
                <th>Mapping</th>
                <th>Expected Account Type</th>
                <th>Selected Account</th>
                <th>Treatment</th>
                <th>Status</th>
              </tr>
            </thead>

            <tbody>
              {journalPreviewRows.map((row) => (
                <tr key={row.mapping_code}>
                  <td>
                    <span
                      className={`pill ${
                        row.side === "Debit" ? "processing" : "approved"
                      }`}
                    >
                      {row.side}
                    </span>
                  </td>
                  <td>{row.mapping_name}</td>
                  <td>{row.expected}</td>
                  <td>{row.selected}</td>
                  <td>{row.treatment}</td>
                  <td>
                    <span
                      className={`pill ${
                        row.status === "configured"
                          ? "approved"
                          : row.status === "needs review"
                          ? "warning"
                          : "draft"
                      }`}
                    >
                      {row.status}
                    </span>
                  </td>
                </tr>
              ))}

              {!journalPreviewRows.length && !busy && (
                <tr>
                  <td colSpan="6">No journal preview rows found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="payroll-card">
        <div className="payroll-card-header">
          <div>
            <h3>Payroll Journal Mappings</h3>
            <p>
              Link each payroll posting bucket to the correct chart of accounts
              ledger. Invalid mappings are blocked to protect accounting
              integrity.
            </p>
          </div>
        </div>

        <div className="payroll-table-wrap">
          <table className="payroll-table">
            <thead>
              <tr>
                <th>Mapping Code</th>
                <th>Mapping Name</th>
                <th>Debit/Credit</th>
                <th>Expected Type</th>
                <th>Suggested Account</th>
                <th>Mapped GL Account</th>
                <th>Validation</th>
                <th>Status</th>
              </tr>
            </thead>

            <tbody>
              {mappingRows.map((row) => (
                <tr
                  key={row.id || row.mapping_code}
                  className={
                    row.account_id && !row.warning ? "mapping-configured" : ""
                  }
                >
                  <td>{row.mapping_code}</td>
                  <td>{row.mapping_name}</td>
                  <td>
                    <span
                      className={`pill ${
                        row.normalSide === "Debit" ? "processing" : "approved"
                      }`}
                    >
                      {row.normalSide}
                    </span>
                  </td>
                  <td>{row.expectedAccountType}</td>
                  <td>
                    {row.suggestion ? (
                      <div className="mapping-suggestion">
                        <span>{accountLabel(row.suggestion)}</span>
                        {!row.account_id ? (
                          <button
                            type="button"
                            className="ghost-btn ghost-btn-sm"
                            onClick={() =>
                              handleMappingChange(row, row.suggestion.id)
                            }
                            disabled={savingMapCode === row.mapping_code}
                          >
                            Use Suggestion
                          </button>
                        ) : null}
                      </div>
                    ) : (
                      <span className="mapping-muted">No safe suggestion</span>
                    )}
                  </td>
                  <td style={{ minWidth: 360 }}>
                    <select
                      value={row.account_id || ""}
                      onChange={(e) =>
                        handleMappingChange(row, e.target.value || null)
                      }
                      disabled={savingMapCode === row.mapping_code}
                    >
                      <option value="">Select account</option>
                      {coaAccounts.map((acc) => (
                        <option key={acc.id} value={acc.id}>
                          {accountLabel(acc)}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    {row.warning ? (
                      <div className="mapping-warning">
                        <AlertTriangle size={14} />
                        <span>{row.warning}</span>
                      </div>
                    ) : row.account_id ? (
                      <div className="mapping-valid">
                        <CheckCircle2 size={14} />
                        <span>Accounting-safe</span>
                      </div>
                    ) : (
                      <span className="mapping-muted">Awaiting mapping</span>
                    )}
                  </td>
                  <td>
                    <span
                      className={`pill ${
                        savingMapCode === row.mapping_code
                          ? "processing"
                          : row.account_id && !row.warning
                          ? "approved"
                          : row.warning
                          ? "warning"
                          : "draft"
                      }`}
                    >
                      {savingMapCode === row.mapping_code
                        ? "saving"
                        : row.account_id && !row.warning
                        ? "configured"
                        : row.warning
                        ? "invalid"
                        : "not mapped"}
                    </span>
                  </td>
                </tr>
              ))}

              {!mappingRows.length && !busy && (
                <tr>
                  <td colSpan="8">No payroll journal mappings found.</td>
                </tr>
              )}

              {busy && (
                <tr>
                  <td colSpan="8">Loading...</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
